const toMin = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    return h * 60 + m;
};
function addMinutesHHMM(hhmm, mins) {
    const total = (toMin(hhmm) + mins) % (24 * 60);
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}
/** Bulk blocks whose interval intersects the rule window on the same weekday. */
export function bulkBlocksOverlapping(blocks, rule) {
    const rStart = toMin(rule.startTime);
    const rEnd = rStart + rule.durationMin;
    return blocks.filter((b) => {
        if (b.state !== "bulk" || b.dayOfWeek !== rule.dayOfWeek)
            return false;
        const bStart = toMin(b.localTime);
        return rStart < bStart + b.durationMin && bStart < rEnd;
    });
}
/** Best-case bookable courts = total minus distinct bulk-blocked courts. */
export function maxBookableAt(blocks, rule, totalCourts) {
    const blocked = new Set(bulkBlocksOverlapping(blocks, rule).map((b) => b.locationId));
    return Math.max(0, totalCourts - blocked.size);
}
/** "Usually unavailable" summary lines for one weekday (bulk only): merge
 *  contiguous courts at the same time-range into `c2–4 · 20:00–21:20`. */
export function summariseBlocks(blocks, dayOfWeek) {
    const bulk = blocks.filter((b) => b.state === "bulk" && b.dayOfWeek === dayOfWeek);
    const byTime = new Map();
    for (const b of bulk) {
        const key = `${b.localTime}|${b.durationMin}`;
        const arr = byTime.get(key) ?? [];
        arr.push(b);
        byTime.set(key, arr);
    }
    const lines = [];
    for (const [key, group] of byTime) {
        const [time, durStr] = key.split("|");
        const dur = Number(durStr);
        const courts = [...new Set(group.map((g) => g.courtNumber))].sort((a, b) => a - b);
        let runStart = courts[0];
        for (let i = 1; i <= courts.length; i++) {
            if (i === courts.length || courts[i] !== courts[i - 1] + 1) {
                const end = courts[i - 1];
                const courtStr = runStart === end ? `c${runStart}` : `c${runStart}–${end}`;
                lines.push(`${courtStr} · ${time}–${addMinutesHHMM(time, dur)}`);
                if (i < courts.length)
                    runStart = courts[i];
            }
        }
    }
    return lines.sort();
}
const minToHHMM = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
/** "3–4" / "2, 5–6" from a list of court numbers (merge consecutive runs). */
function mergeCourtRuns(courts) {
    const sorted = [...new Set(courts)].sort((a, b) => a - b);
    const parts = [];
    let run = sorted[0];
    for (let i = 1; i <= sorted.length; i++) {
        if (i === sorted.length || sorted[i] !== sorted[i - 1] + 1) {
            const end = sorted[i - 1];
            parts.push(run === end ? `${run}` : `${run}–${end}`);
            if (i < sorted.length)
                run = sorted[i];
        }
    }
    return parts.join(", ");
}
/** Distinct courts (by court) referenced by a set of blocks. */
export function blockedCourtCount(blocks) {
    return new Set(blocks.map((b) => b.locationId)).size;
}
/** Compact "Courts 3–4 · 19:20–21:20" lines: merge each court's CONTIGUOUS time
 *  slots into spans, then group courts that share identical spans. Collapses the
 *  6-cell "court 3 @ 19:20 / 20:00 / 20:40 · court 4 @ …" list into one line. */
export function summariseOverlap(blocks) {
    const byCourt = new Map();
    for (const b of blocks) {
        const start = toMin(b.localTime);
        const arr = byCourt.get(b.courtNumber) ?? [];
        arr.push({ start, end: start + b.durationMin });
        byCourt.set(b.courtNumber, arr);
    }
    const spansByCourt = new Map();
    for (const [court, ivs] of byCourt) {
        ivs.sort((a, b) => a.start - b.start);
        const merged = [];
        for (const iv of ivs) {
            const last = merged[merged.length - 1];
            if (last && iv.start <= last.end)
                last.end = Math.max(last.end, iv.end);
            else
                merged.push({ ...iv });
        }
        spansByCourt.set(court, merged);
    }
    const groups = new Map();
    for (const [court, spans] of spansByCourt) {
        const sig = spans.map((s) => `${s.start}-${s.end}`).join(",");
        const arr = groups.get(sig) ?? [];
        arr.push(court);
        groups.set(sig, arr);
    }
    const lines = [];
    for (const courts of groups.values()) {
        const spans = spansByCourt.get(courts[0]);
        const spanStr = spans.map((s) => `${minToHHMM(s.start)}–${minToHHMM(s.end)}`).join(", ");
        lines.push(`Courts ${mergeCourtRuns(courts)} · ${spanStr}`);
    }
    return lines.sort();
}
/** Distinct sampled dates these blocks were taken on, e.g. "19 Jun & 26 Jun". */
export function overlapDates(blocks) {
    const dates = new Set();
    for (const b of blocks)
        for (const o of b.occurrences)
            if (o.taken)
                dates.add(o.date);
    return [...dates]
        .sort()
        .map((d) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" }))
        .join(" & ");
}
/** Human evidence string from a block's occurrences: "booked 22 Jun · free 29 Jun". */
export function evidenceLine(block) {
    return block.occurrences
        .map((o) => `${o.taken ? "booked" : "free"} ${new Date(`${o.date}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`)
        .join(" · ");
}
